/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/AttendanceConfirmationModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";

import styles from "./index.module.css";

const AttendanceConfirmationModal = ({
  open,
  onCancel,
  onSuccess,
  message,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef(null);

  useEffect(() => {
    const handleClose = () => {
      onCancel();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      dialog.showModal();
    } else {
      dialog.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onCancel]);

  return (
    <>
      {open && <div className={styles.overlay}></div>}
      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.closeButtonContainer}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onCancel}
            disabled={isSubmitting}
          >
            &times;
          </button>
        </div>

        <div className={styles.modalContent}>
          <label className={styles.modalTitle}>{message}</label>
        </div>

        <div className={styles.modalButtons}>
          <button
            type="button"
            onClick={onCancel}
            className={styles.cancelButton}
            disabled={isSubmitting}
          >
            {t("pages.attendance.no")}
          </button>
          <button
            type="button"
            className={styles.confirmButton}
            onClick={onSuccess}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={22} thickness={4} color="inherit" />
            ) : (
              t("pages.attendance.yes")
            )}
          </button>
        </div>
      </dialog>
    </>
  );
};

export default AttendanceConfirmationModal;
